import { createSignal, onMount, Show } from 'solid-js';
import { View, Text, hexColor } from "@lightningtv/solid";
import PinKeyboard from './PinKeyboard';
import Header from './Header';
import { fontFamily, isRTL, t } from '../translations/configuration';

export default function SetPin(props: {
    onPinSet: (pin: string) => void;
    onSkip?: () => void;
    showBackground?: boolean;
    showSkip?: boolean;
    x?: number;
    y?: number;
    inputDelay?: number;
    onBackLeft?: () => void;
    keyboardRef?: any;
    autoFocus?: boolean;
    isForFast?: string
}) {
    const [step, setStep] = createSignal<"set" | "confirm" | "success">("set");
    const [firstPin, setFirstPin] = createSignal("");
    const [canAcceptInput, setCanAcceptInput] = createSignal(false);
    const [currentPinLength, setCurrentPinLength] = createSignal(0);
    const keyboardRef: any = props.keyboardRef || {};
    let skipButtonRef: any;
    let enterButtonRef: any;

    const showBackground = props.showBackground !== false;
    const showSkip = props.showSkip !== false && props.onSkip;
    const inputDelay = props.inputDelay || 100;

    onMount(() => {
        setTimeout(() => {
            setCanAcceptInput(true);
        }, inputDelay);

        // Expose focusDoneButton so PinKeyboard can call it when PIN hits 4 digits
        keyboardRef.focusDoneButton = () => {
            enterButtonRef?.setFocus?.();
        };

        // Expose focusEnterButton so keyboard's right-arrow from Del can reach it
        keyboardRef.focusEnterButton = () => {
            enterButtonRef?.setFocus?.();
        };
    });

    const handlePinChange = (pinLength: number) => {
        setCurrentPinLength(pinLength);
        if (pinLength === 4) {
            keyboardRef.focusDoneButton?.();
        }
    };

    const handlePinSubmit = (pin: string) => {
        if (!canAcceptInput()) return;

        if (step() === "set") {
            setFirstPin(pin);
            setStep("confirm");
            setCurrentPinLength(0);
            keyboardRef.resetInput?.();
        } else if (step() === "confirm") {
            if (pin === firstPin()) {
                localStorage.setItem("userPin", pin);
                setStep("success");
                setCurrentPinLength(0);
                keyboardRef.resetInput?.();

                setTimeout(() => {
                    props.onPinSet(pin);
                    setStep("set");
                    setFirstPin("");
                    setCurrentPinLength(0);
                }, props.autoFocus === true || props.isForFast === "true" ? 0 : 2000);
            } else {
                setStep("set");
                setFirstPin("");
                setCurrentPinLength(0);
                keyboardRef.resetInput?.();
            }
        }
    };

    const handleSkip = () => {
        if (!canAcceptInput()) return;
        if (props.onSkip) {
            props.onSkip();
        }
    };

    const shouldShowSkip = () => {
        return showSkip && step() === "set" && currentPinLength() < 4;
    };

    // ── Layout constants ─────────────────────────────────────────────────
    const BOX_W = 110;
    const BOX_H = 80;
    const BOX_GAP = 18;
    const BOXES_TOTAL_W = 4 * BOX_W + 3 * BOX_GAP; // 494
    const BOXES_X = (1920 - BOXES_TOTAL_W) / 2;     // 713

    // Keyboard: 3 cols × 110 + 2 × 16 gaps + 40 padding = 398
    const KB_TOTAL_W = 110 * 3 + 16 * 2 + 40;       // 398
    const KB_X = (1920 - KB_TOTAL_W) / 2;            // 761

    // Enter button — bottom-right corner
    const ENTER_W = 120;
    const ENTER_H = 70;
    const ENTER_X = 1920 - ENTER_W - 50;  // 1780
    const ENTER_Y = 1080 - ENTER_H - 50;  // 940

    // ── PIN boxes ────────────────────────────────────────────────────────
    const PinDots = () => (
        <View x={BOXES_X} y={250}>
            {[0, 1, 2, 3].map((i) => (
                <View
                    x={i * (BOX_W + BOX_GAP)} y={0}
                    width={BOX_W} height={BOX_H}
                    borderRadius={14}
                    color={hexColor(i < currentPinLength() ? "#1c1e22" : "#1c1e22")}
                    style={{
                        borderWidth: 2,
                        borderColor: hexColor(
                            i <= currentPinLength() ? "#1c1e22" : "#1c1e22"
                        ),
                    }}
                >
                    {/* White dot when digit entered */}
                    {i < currentPinLength() && (
                        <View
                            x={Math.floor((BOX_W - 28) / 2)}
                            y={Math.floor((BOX_H - 28) / 2)}
                            width={28} height={28}
                            borderRadius={14}
                            color={hexColor("#e2e8f0")}
                        />
                    )}
                    {/* Cursor on the active empty box */}
                    {i === currentPinLength() && (
                        <View
                            x={Math.floor(BOX_W / 2) - 2}
                            y={14}
                            width={3} height={BOX_H - 28}
                            color={hexColor("#94a3b8")}
                        />
                    )}
                </View>
            ))}
        </View>
    );

    // ── Enter button (bottom-right, outside keyboard) ────────────────────
    const EnterButton = () => (
        <View
            ref={(el) => { enterButtonRef = el; }}
            x={ENTER_X} y={ENTER_Y}
            width={ENTER_W} height={ENTER_H}
            borderRadius={15}
            color={hexColor(currentPinLength() === 4 ? "#2563eb" : "#1b1b20")}
            // style={{
            //     focus: {
            //         color: hexColor(currentPinLength() === 4 ? "#3b82f6" : "#2a3050"),
            //     },
            // }}
            onEnter={() => {
                const pin = (keyboardRef as any)._currentPin?.() ?? "";
                // Use submitPin exposed by PinKeyboard
                keyboardRef.submitPin?.();
            }}
            onLeft={() => { keyboardRef?.setFocus?.(); }}
            onUp={() => { keyboardRef?.setFocus?.(); }}
        >
            <View
                x={45} y={20}
                src="https://i.ibb.co/FbFMtxMw/check.png"
                width={30} height={30}
            />
        </View>
    );

    // ── Keyboard-only (embedded) version ─────────────────────────────────
    const KeyboardOnlyContent = () => (
        <View x={props.x || 0} y={props.y || 0}>
            <Text x={700} y={50} fontSize={36}>
                {step() === "set" ? t("createPin") : t("confirmPin")}
            </Text>

            {step() === "success" && (
                <View x={200} y={50}>
                    <Text fontSize={32} color="#28a745">
                        {("pinSetSuccessfully")}
                    </Text>
                </View>
            )}

            {canAcceptInput() && step() !== "success" ? (
                <PinKeyboard
                    onSubmit={handlePinSubmit}
                    onPinChange={handlePinChange}
                    keyboardRef={keyboardRef}
                    onDown={shouldShowSkip() ? () => skipButtonRef?.setFocus?.() : undefined}
                    onBackLeft={props.onBackLeft}
                    autoFocus={props.autoFocus ? props.autoFocus : false}
                />
            ) : step() !== "success" ? (
                <View y={100}>
                    <Text x={200} y={50} fontSize={24} color={"#666666"}>
                        {t("initializingPin")}
                    </Text>
                </View>
            ) : null}
        </View>
    );

    // ── Full-screen version ───────────────────────────────────────────────
    const FullScreenContent = () => (
        <View color={hexColor("#0d0f14")} width={1920} height={1080}>

            {/* Logo + subtitle */}
            <Header screen={""} />

            <Text x={830} y={150} fontSize={40} style={{ fontFamily: fontFamily() }}>
                {step() === "set"
                    ? t("createPin")
                    : step() === "confirm"
                        ? t("confirmPin")
                        : ""}
            </Text>

            {/* 4 PIN boxes — centered */}
            <PinDots />

            {/* Keyboard — centered */}
            <Show when={canAcceptInput() && step() !== "success"}>
                <View x={KB_X} y={520}>
                    <PinKeyboard
                        onSubmit={handlePinSubmit}
                        onPinChange={handlePinChange}
                        keyboardRef={keyboardRef}
                        onDown={shouldShowSkip() ? () => skipButtonRef?.setFocus?.() : undefined}
                        onBackLeft={props.onBackLeft}
                        autoFocus={props.autoFocus ? props.autoFocus : false}
                    />
                </View>
            </Show>

            {/* Checkmark button — bottom-right corner */}
            <Show when={canAcceptInput() && step() !== "success"}>
                <EnterButton />
            </Show>

            {/* Initializing */}
            <Show when={!canAcceptInput() && step() !== "success"}>
                <View x={820} y={540}>
                    <Text fontSize={24} style={{ color: hexColor("#4b5563") }}>
                        {t("initializingPin")}
                    </Text>
                </View>
            </Show>

            {/* Success */}
            <Show when={step() === "success"}>
                <View x={700} y={500}>
                    <Text fontSize={40} style={{ color: hexColor("#22c55e"), fontWeight: isRTL() ? "normal" : "bold" }}>
                        ✓ PIN set successfully
                    </Text>
                </View>
            </Show>

        </View>
    );

    return showBackground ? <FullScreenContent /> : <KeyboardOnlyContent />;
}